import { api } from "../lib/api";

export type VatScheme = "none" | "flat_rate" | "standard_accrual" | "cash_accounting";
export type FrsTurnoverMethod = "cash" | "invoice";
export type VatStagger = "mar_jun_sep_dec" | "apr_jul_oct_jan" | "may_aug_nov_feb";

export interface Company {
  id: string;
  name: string;
  trading_name: string | null;
  address_line1: string | null;
  address_line2: string | null;
  address_city: string | null;
  address_postcode: string | null;
  address_country: string | null;
  company_number: string | null;
  vat_number: string | null;
  vat_scheme: VatScheme;
  frs_rate: string | null;
  frs_first_year_discount_until: string | null;
  frs_turnover_method: FrsTurnoverMethod;
  vat_stagger: VatStagger | null;
  default_currency: string;
  default_vat_rate: string;
  invoice_prefix: string | null;
  next_invoice_seq: number;
  accounting_year_end: string | null;
  /** Other companies under common control. Divides the Corporation Tax limits. */
  associated_companies_count: number;
  bank_details_text: string | null;
  /** Printed under the totals on invoices whose supply is outside the scope of UK VAT. */
  vat_outside_scope_note: string | null;
  email_from_name: string | null;
  email_from_address: string | null;
  email_bcc_self: boolean;
  /** Fastmail sign-in identity for SMTP AUTH — separate from email_from_address, which
   * may be an alias that isn't itself a Fastmail login (Phase 1b). */
  email_provider_account_id: string | null;
  logo_path: string | null;
  smtp_verified_at: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export type CompanyInput = Omit<
  Company,
  | "id"
  | "next_invoice_seq"
  | "logo_path"
  | "smtp_verified_at"
  | "archived_at"
  | "created_at"
  | "updated_at"
>;

export function listCompanies(includeArchived = false): Promise<Company[]> {
  return api.get<Company[]>(`/api/companies?include_archived=${includeArchived}`);
}

export function getCompany(id: string): Promise<Company> {
  return api.get<Company>(`/api/companies/${id}`);
}

export function createCompany(input: CompanyInput): Promise<Company> {
  return api.post<Company>("/api/companies", input);
}

export function updateCompany(
  id: string,
  input: CompanyInput & { updated_at: string },
): Promise<Company> {
  return api.put<Company>(`/api/companies/${id}`, input);
}

export function archiveCompany(id: string): Promise<void> {
  return api.delete(`/api/companies/${id}`);
}

export function uploadLogo(id: string, file: File): Promise<Company> {
  return api.upload<Company>(`/api/companies/${id}/logo`, file);
}

export function updateSmtp(
  id: string,
  emailProviderAccountId: string | null,
  smtpPassword: string,
): Promise<Company> {
  return api.put<Company>(`/api/companies/${id}/smtp`, {
    smtp_password: smtpPassword,
    email_provider_account_id: emailProviderAccountId,
  });
}
